import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getConfig } from '@shared/config/runtime'
import { tokenProvider } from './auth/tokenProvider'
import { toAppError, type ApiErrorBody } from './errors'

interface HttpHooks {
  getLocale: () => string
  onSessionExpired: () => void
}

let hooks: HttpHooks = { getLocale: () => 'en', onSessionExpired: () => {} }

/** Wire app-level concerns in without coupling the client to Pinia or the router. */
export function configureHttp(next: Partial<HttpHooks>) {
  hooks = { ...hooks, ...next }
}

/** The single Axios instance. Feature api.ts files use it; components never do. */
// Arrays as repeated keys (status=A&status=B), matching the URL-synced list filters
export const http = axios.create({ timeout: 30_000, paramsSerializer: { indexes: null } })

declare module 'axios' {
  interface AxiosRequestConfig {
    /** credential endpoints: no bearer token and no 401 → refresh → retry */
    skipAuthRefresh?: boolean
  }
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

http.interceptors.request.use(async (config) => {
  config.baseURL ??= getConfig().apiBaseUrl
  if (!config.skipAuthRefresh) {
    const token = await tokenProvider().getAccessToken()
    if (token) config.headers.Authorization = `Bearer ${token}`
  }
  config.headers['X-Correlation-Id'] = crypto.randomUUID()
  config.headers['Accept-Language'] = hooks.getLocale()
  return config
})

http.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetriableConfig | undefined
    if (error.response?.status === 401 && config && !config._retried && !config.skipAuthRefresh) {
      config._retried = true
      const token = await tokenProvider().refresh()
      if (token) return http(config)
      hooks.onSessionExpired()
    }
    // never leak a raw AxiosError to components
    throw toAppError(error)
  },
)
